import * as React from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import CustomPagination from '../../../components/forms/Pagination';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, SvgIcon, TableSortLabel, Tooltip, Typography, useTheme } from '@mui/material';
import { Link } from 'react-router-dom';
import useNswagClient from '../../../hooks/api/useNswagClient';
import InfoIcon from '@mui/icons-material/Info';
import { TransactionRecord } from '../types/TransactionRecord';
import RoundAndFormatValue from '../../../components/shared/RoundAndFormatValue';
import { useEffect, useState, useContext } from 'react';
import { NoteModal } from './modals/TransactionNoteModal';
import useCurrencyCodeConfiguration from '../../../hooks/configurations/useCurrencyCodeConfiguration';
import { Minus, Plus, Trash01 } from '../../../assets';
import { Pill } from '../../../components/shared/Pill';
import { TransactionsContext, TransactionsContextValues } from '../contexts/TransactionsContext';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { TableLoading } from '../../../components/forms/TableLoading';
import { EmptyRows } from '../../../components/forms/EmptyRows';
import { TableHeader } from '../../../components/forms/TableHeader';
import { UserContext } from '../../../components/shared/useUser';
import { isDemoModeEnabled } from '../../../utils/featureAccess';

const HeaderTableCell = (props: { dataCol: string, colDisplay: string, sort: boolean, fieldToSort: string | undefined, direction: 'desc' | 'asc' | undefined, handleSort: Function, align?: 'center' | 'left' }) => {
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}
      align={props.align ?? 'left'}>
      {props.sort ? <TableSortLabel
        active={props.fieldToSort === props.dataCol}
        hideSortIcon={props.fieldToSort != props.dataCol}
        direction={props.direction}
        onClick={() => props.handleSort(props.dataCol)}
      >
        {props.colDisplay}
      </TableSortLabel>
        :
        <>{props.colDisplay}</>}
    </TableCell>
  );
};

const BodyTableCell = (props: { children?: React.ReactNode, align?: 'center' | 'left', minWidth?: string, clampedText?: boolean }) => {
  return (
    <TableCell
      sx={{
        textAlign: props.align ?? 'left',
        minWidth: props.minWidth,
        py: 0,
      }}
      variant={props.clampedText ? 'clampedText' : 'body'}
    >
      {props.children}
    </TableCell >
  );
};

const DenseTable = () => {
  const { fieldToSort,
    sortDirection,
    setSortDirection,
    setFieldToSort,
    transactions,
    reloadTransactions,
    totalCount,
    isLoading,
    page,
    changePage,
    lastSearchTerm,
    handleClearButtonClick,
    handleConfirmModal,
  } = React.useContext(TransactionsContext) as TransactionsContextValues;

  const { t } = useTranslation('common');
  const theme = useTheme();
  const { voidproduct } = useNswagClient();
  const [openModal, setOpenModal] = useState(false);
  const [openNoteModal, setOpenNoteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(-1);
  const [note, setNote] = useState('');
  const { currencyCode, getCurrencyCodeConfiguration } = useCurrencyCodeConfiguration();
  const [openMaterials, setOpenMaterials] = useState<{ [index: number]: boolean }>({});
  const [showVoidedTransactionModal, setShowVoidedTransactionModal] = useState(false);
  const [errorVoidTransaction, setErrorVoidTransaction] = useState('');
  const { hasPermissionTo, selectedStore } = useContext(UserContext);
  const franchisesThatRequirePermission = ['NE Apple Green GB', 'Chick-fil-A Singapore', 'Chick-fil-A UK'];
  const canViewRecipeDetails = hasPermissionTo(['TransactionDetailsRead']) ||
    !franchisesThatRequirePermission.includes(selectedStore?.franchiseName || '');
  const isDemoMode = isDemoModeEnabled();

  const isAsc = sortDirection === 'asc';
  const BORDER_STYLE = `2px solid ${theme.palette.custom.gray[300]}`;

  useEffect(() => {
    getCurrencyCodeConfiguration();
  }, []);

  const handleSort = (property: string) => {
    setSortDirection(isAsc ? 'desc' : 'asc');
    setFieldToSort(property);
    setOpenMaterials({});
  };

  const submitButtonHandler = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number) => {
    event.stopPropagation();
    setDeleteId(id);
    setOpenModal(true);
  };

  const handleCancelModal = () => {
    setOpenModal(false);
  };

  const handleConfirmVoidModal = async () => {
    const trans = transactions.find(x => x.id == deleteId);
    if (trans) {
      const data = {
        id: trans.productID,
        transactionDay: trans.transactionDay,
      };

      voidproduct(data).then(() => {
        reloadTransactions();
        setOpenModal(false);
      }).catch((error) => {
        if (error?.includes('Transaction already voided')) {
          setErrorVoidTransaction(error);
          setShowVoidedTransactionModal(true);
        }
      });
    }
  };

  const handleNoteButtonClick = (note: string) => {
    setNote(note);
    setOpenNoteModal(!openNoteModal);
  };

  const OpenNoteModalRevert = () => {
    setOpenNoteModal(!openNoteModal);
  };

  const maxPages = () => {
    return Math.ceil(totalCount / 10);
  };

  const handlePlusButtonClick = (index: number) => {
    // Close all open material sections
    const updatedOpenMaterials: { [index: number]: boolean } = {};
    Object.keys(openMaterials).forEach((key) => {
      updatedOpenMaterials[parseInt(key)] = false;
    });

    // Open the clicked material section
    setOpenMaterials((prevState) => ({
      ...updatedOpenMaterials,
      [index]: !prevState[index],
    }));
  };

  const viewRawTransactionData = (transaction: TransactionRecord) => {

    if (transaction.sourceID === '') {
      return;
    }
    else if (transaction.source === 'pos') {
      return `/store/transactions/source/${transaction.sourceID}/${transaction?.transactioDate?.toISOString().substring(0, 10)}`;
    }
    else if (transaction.transactionType === 'Transfer') {
      if ((transaction?.sourceID?.match(/-/g) || []).length <= 1) {
        const source = transaction?.sourceID?.substring(transaction.sourceID.indexOf('-') + 1);
        return `/store/stock-transfer/${source}`;
      }
    }
    else if (transaction.transactionType === 'Stock Adjustment') {
      if ((transaction?.sourceID?.match(/-/g) || []).length === 1) {
        const source = transaction?.sourceID?.substring(transaction.sourceID.indexOf('-') + 1);
        return `/store/review-stock-count/${source}`;
      }
    }
  };

  if (transactions.length == 0 && !isLoading) {
    return <EmptyTable bodyRowHeight={40}
      headerHeight={45}
      text={t('transactions.title')}
      searchTerm={lastSearchTerm}
      onClear={() => { handleClearButtonClick(); }}
      onAdd={() => handleConfirmModal()}
      onAddIsCreating={false}
      addItemText={t('createNewTransactionBtn')}
      onAddIsDisabled={false} />;
  }

  return (
    <>
      <TableHeader>
        {t('transactions.title')}
        {totalCount > 0 && <Pill text={`${totalCount} ${t('transactions.title')}`}></Pill>}
      </TableHeader>
      {isLoading ? <TableLoading headerHeight={45}
        bodyRowHeight={40}
        minTableWidth={950} /> :
        <TableContainer>
          <Table sx={{ minWidth: '950px' }}>
            <TableHead>
              <TableRow sx={{ minHeight: '65px' }}>
                <TableCell></TableCell>
                <HeaderTableCell dataCol='itemNumber'
                  colDisplay={t('transactions.sku')}
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='description'
                  colDisplay={t('transactions.itemName')}
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='type'
                  colDisplay={t('transactions.transactionTypes')}
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='date'
                  colDisplay={t('transactions.transactionDate')}
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='quantity'
                  colDisplay={t('transactions.quantity')}
                  sort={false}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='store'
                  colDisplay={t('transactions.store')}
                  sort={false}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='reason'
                  colDisplay={t('transactions.transactionReasons')}
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='source'
                  colDisplay={t('transactions.source')}
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='sourceID'
                  colDisplay={t('transactions.sourceID')}
                  sort={false}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='costPrice'
                  colDisplay={t('transactions.actualCost')}
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                <HeaderTableCell dataCol='note'
                  colDisplay={t('transactions.note')}
                  align='center'
                  sort={true}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
                {isDemoMode && (
                  <HeaderTableCell dataCol='document'
                    colDisplay='Document'
                    align='center'
                    sort={false}
                    handleSort={handleSort}
                    direction={sortDirection}
                    fieldToSort={fieldToSort}
                  />
                )}
                <HeaderTableCell dataCol='x'
                  colDisplay={''}
                  align='center'
                  sort={false}
                  handleSort={handleSort}
                  direction={sortDirection}
                  fieldToSort={fieldToSort}
                />
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((row, index) => (
                <React.Fragment key={row.id?.toString() + '-' + row.SkU}>
                  <TableRow
                    sx={openMaterials[index ?? -1] ? { borderTop: BORDER_STYLE, borderLeft: BORDER_STYLE, borderRight: BORDER_STYLE } : {}}>
                    <BodyTableCell>
                      {canViewRecipeDetails && (
                        <IconButton onClick={(event) => { event.stopPropagation(); handlePlusButtonClick(index ?? 0); }}
                          size='small'>
                          {openMaterials[index ?? -1] ? <Minus /> : <Plus />}
                        </IconButton>
                      )}
                    </BodyTableCell>
                    <BodyTableCell>{row.SkU}</BodyTableCell>
                    <BodyTableCell minWidth='160px'
                      clampedText={true}>
                      <Typography variant='textXS'>
                        {row.itemName}
                      </Typography>
                    </BodyTableCell>
                    <BodyTableCell>{row.transactionType}</BodyTableCell>
                    <BodyTableCell>{row.transactioDate?.toLocaleString()}</BodyTableCell>
                    <BodyTableCell>{row.quantity}</BodyTableCell>
                    <BodyTableCell>{row.store}</BodyTableCell>
                    <BodyTableCell>{row.reason}</BodyTableCell>
                    <BodyTableCell>{row.source}</BodyTableCell>
                    <BodyTableCell>
                      <Tooltip title={row.sourceID}>
                        {viewRawTransactionData(row) ? (
                          <Link
                            to={viewRawTransactionData(row) || '#'}
                            style={{ cursor: 'pointer', color: '#1976d2', textDecoration: 'none' }}
                          >
                            {row.sourceID?.split('-')[row.sourceID?.split('-').length - 1]}
                          </Link>
                        ) : (
                          <span>{row.sourceID?.split('-')[row.sourceID?.split('-').length - 1]}</span>
                        )}
                      </Tooltip>
                    </BodyTableCell>
                    <BodyTableCell>
                      <RoundAndFormatValue value={row.actualCost ?? 0}
                        currencyCode={currencyCode} />
                    </BodyTableCell>
                    <BodyTableCell align='center'>
                      {row.note && (
                        <Tooltip title={row.note}>
                          <IconButton aria-label={t('showNote')}
                            size='small'
                            onClick={() => handleNoteButtonClick(row.note ?? '')}>
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </BodyTableCell>
                    {isDemoMode && (
                      <BodyTableCell align='center'>
                        {row.documentId && (
                          <Tooltip title="View CCTV footage">
                            <Link
                              to={`/documents/view/${row.documentId}`}
                              style={{ cursor: 'pointer', fontSize: '12px', color: '#1976d2', textDecoration: 'none' }}
                            >
                              CCTV
                            </Link>
                          </Tooltip>
                        )}
                      </BodyTableCell>
                    )}
                    <BodyTableCell align='center'>
                      {row.reason != 'Void' && (row.transactionType === 'Sale' || row.transactionType === 'Waste' || row.transactionType === 'Made in Store') && (
                        <Tooltip title={t('transactions.voidTransaction')}>
                          <IconButton size='small'
                            aria-label={t('transactions.voidTransaction')}
                            onClick={(event) => submitButtonHandler(event, row.id ?? -1)}>
                            <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01></Trash01></SvgIcon>
                          </IconButton>
                        </Tooltip>
                      )}
                    </BodyTableCell>
                  </TableRow>
                  {
                    openMaterials[index ?? -1] && (
                      <>
                        <TableRow
                          sx={{
                            borderLeft: openMaterials[index ?? -1] ? BORDER_STYLE : 'none',
                            borderRight: openMaterials[index ?? -1] ? BORDER_STYLE : 'none',
                          }}>
                          <TableCell colSpan={isDemoMode ? 13 : 12}
                            sx={{
                              backgroundColor: theme.palette.custom.gray[300],
                            }}>
                            <Typography variant='textLG'
                              ml='50px'>
                              {t('transactions.transactionMaterials')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                        {row.materials?.map((rowMaterial, index) => (
                          <React.Fragment key={`${row.id}_${index}`}>
                            <TableRow
                              sx={{
                                borderBottom: index === (row.materials && row.materials.length - 1) ? BORDER_STYLE : 'none',
                                borderLeft: BORDER_STYLE,
                                borderRight: BORDER_STYLE,
                                minHeight: '53px',
                              }}>
                              <BodyTableCell></BodyTableCell>
                              <BodyTableCell>{rowMaterial.itemNumber}</BodyTableCell>
                              <BodyTableCell>{rowMaterial.itemDescription}</BodyTableCell>
                              <BodyTableCell>{row.transactionType}</BodyTableCell>
                              <BodyTableCell>{row.transactioDate?.toLocaleString()}</BodyTableCell>
                              <BodyTableCell>{rowMaterial.quantity}</BodyTableCell>
                              <BodyTableCell>{row.store}</BodyTableCell>
                              <BodyTableCell>{row.reason}</BodyTableCell>
                              <BodyTableCell>{row.source}</BodyTableCell>
                              <BodyTableCell>
                                <Tooltip title={row.sourceID}>
                                  <Box>{row.sourceID?.split('-')[row.sourceID?.split('-').length - 1]}</Box>
                                </Tooltip>
                              </BodyTableCell>
                              <BodyTableCell>
                                <RoundAndFormatValue value={rowMaterial.costPrice ?? 0}
                                  currencyCode={currencyCode} />
                              </BodyTableCell>
                              <BodyTableCell align='center'>
                                {rowMaterial.note && (
                                  <Tooltip title={rowMaterial.note}>
                                    <IconButton aria-label={t('showNote')}
                                      onClick={() => handleNoteButtonClick(rowMaterial.note ?? '')}>
                                      <InfoIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </BodyTableCell>
                              {isDemoMode && <BodyTableCell align='center'></BodyTableCell>}
                            </TableRow>
                          </React.Fragment>
                        ))}
                        {/* make next row the colour it already is */}
                        {(row?.materials?.length ?? 0) % 2 === 0 && <TableRow sx={{ height: 0 }}>
                          <TableCell colSpan={isDemoMode ? 13 : 12}
                            sx={{ height: 0, p: 0 }}>
                          </TableCell>
                        </TableRow>}
                      </>
                    )
                  }
                </React.Fragment>
              ))}

              <EmptyRows numberOfCurrentRows={transactions?.length ?? 0}
                height={40} />
            </TableBody>
          </Table>
        </TableContainer >}

      <CustomPagination useNoDataMessage={false}
        page={page}
        setPage={changePage}
        maxPages={maxPages()}
        breakpointForChangeDisplay={120000}
      />
      <ConfirmationDialog open={openModal}
        message={t('deleteModalMessage')}
        onConfirm={handleConfirmVoidModal}
        onCancel={handleCancelModal}
        icon={<Trash01 />}
        title={t('transactions.deleteConfirmText')}
        variant='bad'
        confirmButtonText={t('confirm')}
        cancelButtonText={t('cancel')}
      />
      <NoteModal
        note={note}
        open={openNoteModal}
        onClose={OpenNoteModalRevert}
      />
      <MessagesSnackbar severity='error'
        open={showVoidedTransactionModal}
        onClose={() => setShowVoidedTransactionModal(false)}
        message={errorVoidTransaction}
        duration={60000} />
    </>
  );
};

export default DenseTable;